import { useSyncExternalStore } from 'react';

export const TIER_STORAGE_KEY = 'asm_tier';
export const DEFAULT_TIER = 'enterprise';

export const TIERS = {
  starter: {
    name: 'Starter',
    price: '$8/user/mo',
    features: [
      'activity_monitoring',
      'web_monitoring',
      'basic_alerts',
      '5_day_retention',
      'email_support',
    ],
    limits: { employees: 25, devices: 50, retention_days: 5, screenshots_per_day: 10 },
  },
  professional: {
    name: 'Professional',
    price: '$12/user/mo',
    features: [
      'activity_monitoring',
      'web_monitoring',
      'file_monitoring',
      'usb_detection',
      'ai_tool_tracking',
      'advanced_alerts',
      '30_day_retention',
      'scheduled_reports',
      'slack_notifications',
      'priority_support',
    ],
    limits: { employees: 100, devices: 200, retention_days: 30, screenshots_per_day: 50 },
  },
  enterprise: {
    name: 'Enterprise',
    price: '$15/user/mo',
    features: [
      'activity_monitoring',
      'web_monitoring',
      'file_monitoring',
      'usb_detection',
      'ai_tool_tracking',
      'git_monitoring',
      'advanced_alerts',
      'risk_scoring',
      '365_day_retention',
      'scheduled_reports',
      'pdf_reports',
      'all_notifications',
      'remote_desktop',
      'sso',
      'legal_hold',
      'gdpr_tools',
      'api_access',
      'dedicated_support',
    ],
    limits: { employees: -1, devices: -1, retention_days: 365, screenshots_per_day: -1 },
  },
};

const TIER_ORDER = ['starter', 'professional', 'enterprise'];

const FEATURE_OVERRIDES = {
  professional: ['risk_scoring'],
};

export const FEATURE_LABELS = {
  activity_monitoring: 'Activity Monitoring',
  web_monitoring: 'Web Monitoring',
  basic_alerts: 'Basic Alerts',
  advanced_alerts: 'Advanced Alerts',
  file_monitoring: 'File Monitoring',
  usb_detection: 'USB Detection',
  ai_tool_tracking: 'AI Tool Tracking',
  risk_scoring: 'Advanced Risk Scoring',
  scheduled_reports: 'Scheduled Reports',
  slack_notifications: 'Slack Notifications',
  all_notifications: 'All Notification Channels',
  remote_desktop: 'Remote Desktop',
  sso: 'Single Sign-On',
  legal_hold: 'Legal Hold',
  gdpr_tools: 'GDPR Tooling',
  api_access: 'API Access',
  pdf_reports: 'PDF Reports',
  git_monitoring: 'Git Monitoring',
  priority_support: 'Priority Support',
  dedicated_support: 'Dedicated Support',
  email_support: 'Email Support',
};

function resolveTierName(name) {
  return TIERS[name] ? name : DEFAULT_TIER;
}

function canUseDOM() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function formatFeatureName(feature) {
  if (!feature) return 'Premium Feature';
  return FEATURE_LABELS[feature] || String(feature).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getTierByName(name) {
  const tierName = resolveTierName(name);
  return { id: tierName, ...TIERS[tierName] };
}

export function getCurrentTierName() {
  if (!canUseDOM()) return DEFAULT_TIER;

  const storedTier = window.localStorage.getItem(TIER_STORAGE_KEY);
  const resolvedTier = resolveTierName(storedTier || DEFAULT_TIER);

  if (storedTier !== resolvedTier) {
    window.localStorage.setItem(TIER_STORAGE_KEY, resolvedTier);
  }

  return resolvedTier;
}

export function setCurrentTierName(name) {
  const resolvedTier = resolveTierName(name);

  if (canUseDOM()) {
    window.localStorage.setItem(TIER_STORAGE_KEY, resolvedTier);
    window.dispatchEvent(new Event('asm:tier-change'));
  }

  return resolvedTier;
}

export function hasFeature(tier, feature) {
  const tierName = resolveTierName(typeof tier === 'string' ? tier : tier?.id);
  const tierConfig = TIERS[tierName];
  const features = new Set([...(tierConfig.features || []), ...(FEATURE_OVERRIDES[tierName] || [])]);
  return features.has(feature);
}

export function getUpgradeTier(currentTier, feature) {
  const currentTierName = resolveTierName(currentTier);

  if (hasFeature(currentTierName, feature)) {
    return getTierByName(currentTierName);
  }

  const upgradeTierName =
    TIER_ORDER.find((tierName) => hasFeature(tierName, feature)) || DEFAULT_TIER;

  return getTierByName(upgradeTierName);
}

function subscribe(callback) {
  if (!canUseDOM()) {
    return () => {};
  }

  let previousTier = getCurrentTierName();

  const notifyIfChanged = () => {
    const nextTier = getCurrentTierName();

    if (nextTier !== previousTier) {
      previousTier = nextTier;
      callback();
    }
  };

  const intervalId = window.setInterval(notifyIfChanged, 1000);

  window.addEventListener('storage', notifyIfChanged);
  window.addEventListener('focus', notifyIfChanged);
  window.addEventListener('visibilitychange', notifyIfChanged);
  window.addEventListener('asm:tier-change', notifyIfChanged);

  return () => {
    window.clearInterval(intervalId);
    window.removeEventListener('storage', notifyIfChanged);
    window.removeEventListener('focus', notifyIfChanged);
    window.removeEventListener('visibilitychange', notifyIfChanged);
    window.removeEventListener('asm:tier-change', notifyIfChanged);
  };
}

export function useCurrentTier() {
  return useSyncExternalStore(subscribe, getCurrentTierName, () => DEFAULT_TIER);
}
